import { Box, Button, Divider, IconButton, List, useMediaQuery, useTheme } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useMemo, type ReactNode } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Tag_Rule_Entity_Type_Enum } from '@kheops/graphql';
import FullTextSearch from '../../common/components/FullTextSearch';
import Sheet from '../../common/components/Sheet';
import { FeatureFlagKey, isFeatureEnabled } from '../../common/utils/featureFlags';
import DiscoveryDepartmentsNavigation from '../navigation/DiscoveryDepartmentsNavigation';
import useOpenable from '../../hooks/useOpenable';
import GeoLocationFilter from '../../search/filters/GeoLocationFilter';
import ProductLabelsFilter from '../filters/ProductLabelsFilter';
import DiscoveryViewSwitch from '../DiscoveryViewSwitch';
import { discoveryViewAtom } from '../state';
import SocialTagFilter from './filters/SocialTagFilter';
import CompanyCategoryFilter from './filters/CompanyCategoryFilter';
import CompanyDeliveryMovFilter from './filters/CompanyDeliveryMovFilter';
import ClassificationTagFilter from './filters/ClassificationTagFilter';
import { CLASSIFICATION_TAG_FILTERS, CLASSIFICATION_TAG_SHEET_FILTERS } from './filters/classificationTagFilters';

export interface DiscoveryFiltersProps {
  fullTextSearchPlaceholder: string;
  hideLabelsFilter?: boolean;
  index: string;
  resultsLabel?: ReactNode;
}

export default function DiscoveryFilters({ fullTextSearchPlaceholder, hideLabelsFilter, index, resultsLabel }: DiscoveryFiltersProps): React.JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation(['common', 'discovery', 'search', 'product-classification-tags']);
  const tagItemLabel = (value: string): string => t(`product-classification-tags:${value}`);
  const { indexUiState } = useInstantSearch();
  const { isOpen, open, close } = useOpenable();
  const discoveryView = useAtomValue(discoveryViewAtom);
  const classificationEnabled = isFeatureEnabled(FeatureFlagKey.PRODUCT_CLASSIFICATION);

  const tagEntity = useMemo((): Tag_Rule_Entity_Type_Enum => {
    return discoveryView === 'SUPPLIERS' ? Tag_Rule_Entity_Type_Enum.Supplier : Tag_Rule_Entity_Type_Enum.BaseUnit;
  }, [discoveryView]);
  const classificationRowFilters = discoveryView === 'SUPPLIERS'
    ? CLASSIFICATION_TAG_FILTERS.filter(({ attribute }) => attribute === 'tags.EVENEMENT')
    : CLASSIFICATION_TAG_FILTERS;

  const filterPills = (
    <>
      {isMobile ? (
        <IconButton
          aria-label={t('search:all_filters')}
          onClick={open}
          sx={{
            width: 40,
            height: 40,
            color: 'onSurfaceVariant.main',
            flexShrink: 0,
          }}
        >
          <TuneOutlinedIcon />
        </IconButton>
      ) : (
        <Button
          variant="text"
          startIcon={<TuneIcon />}
          onClick={open}
        >
          {t('search:all_filters')}
        </Button>
      )}
      {discoveryView === 'SUPPLIERS' && (
        <>
          <CompanyDeliveryMovFilter mode="POPOVER" />
          <CompanyCategoryFilter mode="POPOVER" />
        </>
      )}
      <SocialTagFilter
        entity={tagEntity}
        level={1}
        label={t('discovery:attractivity')}
        mode="POPOVER"
      />
      <SocialTagFilter
        entity={tagEntity}
        level={2}
        label={t('discovery:advantages')}
        mode="POPOVER"
      />
      {classificationEnabled && classificationRowFilters.map(({ attribute, labelKey }) => (
        <ClassificationTagFilter
          key={attribute}
          attribute={attribute}
          label={t(labelKey)}
          mode="POPOVER"
          getItemLabel={tagItemLabel}
        />
      ))}
    </>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'start' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: { xs: 1, lg: 1.5 },
          }}
        >
          {isFeatureEnabled(FeatureFlagKey.PRODUCT_CLASSIFICATION) && (
            <DiscoveryDepartmentsNavigation />
          )}
          <FullTextSearch
            inputProps={{
              placeholder: fullTextSearchPlaceholder,
              fullWidth: true,
              sx: classificationEnabled
                ? { flex: 1, width: '100%', minWidth: 0 }
                : { width: { lg: '623px' } },
            }}
            value={indexUiState.query}
          />
          <Box
            sx={{
              display: {
                xs: 'none',
                lg: 'flex',
              },
            }}
          >
            <DiscoveryViewSwitch />
          </Box>
        </Box>
        {classificationEnabled && isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {resultsLabel}
            <DiscoveryViewSwitch />
          </Box>
        )}
        {classificationEnabled ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'auto' }}>
              {filterPills}
            </Box>
            {resultsLabel && !isMobile && (
              <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                {resultsLabel}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%', overflow: 'auto' }}>
            {filterPills}
          </Box>
        )}
      </Box>
      <Sheet
        keepMounted
        open={isOpen}
        onClose={close}
        width={320}
        title={t('common:filters')}
        titleIcon={<TuneOutlinedIcon />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <List sx={{ pt: 1.5, flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
            <GeoLocationFilter mode="ACCORDION" index={index} />
            <Divider />
            {!hideLabelsFilter && (
              <>
                <ProductLabelsFilter mode="ACCORDION" />
                <Divider />
              </>
            )}
            {discoveryView === 'SUPPLIERS' && (
              <>
                <CompanyDeliveryMovFilter mode="ACCORDION" />
                <Divider />
                <CompanyCategoryFilter mode="ACCORDION" />
                <Divider />
              </>
            )}
            <SocialTagFilter
              entity={tagEntity}
              level={1}
              label={t('discovery:attractivity')}
              mode="ACCORDION"
            />
            <Divider />
            <SocialTagFilter
              entity={tagEntity}
              level={2}
              label={t('discovery:advantages')}
              mode="ACCORDION"
            />
            {classificationEnabled && CLASSIFICATION_TAG_SHEET_FILTERS.map(({ attribute, labelKey }) => (
              <ClassificationTagFilter
                key={attribute}
                attribute={attribute}
                label={t(labelKey)}
                mode="ACCORDION"
                getItemLabel={tagItemLabel}
              />
            ))}
          </List>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, pb: 1 }}>
            <Button variant="text" onClick={close}>
              {t('common:cancel')}
            </Button>
            <Button variant="contained" onClick={close}>
              {t('common:validate')}
            </Button>
          </Box>
        </Box>
      </Sheet>
    </Box>
  );
}
